import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Button, Box, Chip, IconButton, Tooltip
} from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { homeUrl } from '../proxy-config';

export default function NavBar() {
  const { user, login, logout } = useAuth();
  const { isDark, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const isAdmin = location.pathname.startsWith('/admin');

  const navColor = (active) => ({
    color: active ? 'secondary.main' : headerText,
    fontSize: '0.85rem',
    fontWeight: active ? 600 : 400,
    opacity: active ? 1 : 0.7,
    '&:hover': { opacity: 1 },
    letterSpacing: '0.03em'
  });

  // Header follows app design system (matches Resume Optimizer)
  const headerBg = isDark ? '#0F1520' : '#1A2B4A';
  const headerText = isDark ? '#E8E0D0' : '#F5F0E8';

  return (
    <AppBar position="static" sx={{ backgroundColor: headerBg, borderBottom: isDark ? '1px solid rgba(200,148,42,0.12)' : 'none' }} elevation={0}>
      <Toolbar sx={{ minHeight: 56 }}>
        {/* Logo — links to company home page */}
        <Box
          component="a"
          href={homeUrl}
          sx={{ mr: 4, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textDecoration: 'none' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <Typography component="span" sx={{
              fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 22,
              color: headerText, letterSpacing: '-0.01em'
            }}>
              Raike
            </Typography>
            <Typography component="span" sx={{
              fontFamily: "'EB Garamond', serif", fontStyle: 'italic', fontWeight: 300,
              fontSize: 28, color: 'secondary.main', mx: '3px', lineHeight: 1
            }}>
              &amp;
            </Typography>
            <Typography component="span" sx={{
              fontFamily: "'Playfair Display', serif", fontWeight: 400, fontSize: 22,
              color: headerText
            }}>
              Sons
            </Typography>
          </Box>
          <Typography sx={{
            fontSize: '0.5rem', fontWeight: 600,
            letterSpacing: '0.25em', textTransform: 'uppercase',
            color: 'secondary.main', opacity: 0.7, mt: '-2px'
          }}>
            Billing
          </Typography>
        </Box>

        {/* Navigation */}
        <Box sx={{ flexGrow: 1, display: 'flex', gap: 0.5 }}>
          <Button sx={navColor(isActive('/plans'))} onClick={() => navigate('/plans')}>
            Plans
          </Button>
          {user && (
            <>
              <Button sx={navColor(isActive('/subscriptions'))} onClick={() => navigate('/subscriptions')}>
                My Subscriptions
              </Button>
              <Button sx={navColor(isActive('/payments'))} onClick={() => navigate('/payments')}>
                Payment History
              </Button>
              {user.isAdmin && (
                <Button sx={navColor(isAdmin)} onClick={() => navigate('/admin')}>
                  Admin
                </Button>
              )}
            </>
          )}
        </Box>

        {/* Right side */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={isDark ? 'Light mode' : 'Dark mode'}>
            <IconButton onClick={toggleMode} size="small" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} sx={{ color: headerText, opacity: 0.6, '&:hover': { opacity: 1 } }}>
              {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {user ? (
            <>
              <Typography variant="body2" sx={{ color: headerText, fontSize: '0.8rem', opacity: 0.8 }}>
                {user.name || user.email}
              </Typography>
              {user.isAdmin && (
                <Chip label="Admin" size="small" sx={{
                  backgroundColor: 'rgba(200,148,42,0.2)',
                  color: 'secondary.main',
                  fontWeight: 600, fontSize: '0.65rem', height: 20
                }} />
              )}
              <Button
                onClick={logout}
                startIcon={<LogoutIcon fontSize="small" />}
                size="small"
                sx={{
                  color: headerText,
                  opacity: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': { opacity: 1, color: 'error.main' }
                }}
              >
                Sign Out
              </Button>
            </>
          ) : (
            <Button
              onClick={login}
              size="small"
              sx={{
                color: headerText,
                fontSize: '0.8rem',
                fontWeight: 500,
                textTransform: 'none',
                opacity: 0.8,
                '&:hover': { opacity: 1 }
              }}
            >
              Sign In
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}
